import {
  Box,
  Button,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { parseSomething } from "../../utils/parseSomething";
import { useDispatch, useSelector } from "react-redux";
import { isValidMotionProp, motion, AnimatePresence } from "framer-motion";
import { COMMON_WEB_PATH } from "../../constant";
import Count from "./components/Count";
import dayjs from "dayjs";
import useSound from "../../hook/useSound";
import {
  CLEAR_SOCKET_MESSAGE,
  notice,
  SAVE_SOCKET_MESSAGE,
} from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import {
  storeCurrencyExchange,
  triggerTransferGivenUpPoint,
  SHOW_MAINTAIN_ALERT,
} from "../../redux/action/member/memberAction";
import earningMusic from "../../images/music/earning-money.mp3";
import useCheckLogin from "../../hook/useCheckLogin";
import CommonAlertModal from "../../components/Common/commonAlertModal";
import EndGameDetail from "../../components/EndGameDetail/EndGameDetail";

const gradientStyle = {
  background:
    "linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(247,249,79,1) 0%, rgba(254,238,54,1) 50%, rgba(245,174,12,1) 75%, rgba(236,208,59,1) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const animationVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
    y: -20,
    rotate: -10,
  },
  visible: {
    scale: 1.5,
    opacity: 1,
    y: 0,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 15,
      mass: 0.8,
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94], // cubic-bezier for smooth easing
      when: "beforeChildren",
      staggerChildren: 0.15,
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    y: 20,
    rotate: 5,
    transition: {
      duration: 0.3,
      ease: [0.55, 0.085, 0.68, 0.53], // smooth exit easing
    },
  },
};

const HEARTBEAT_INTERVAL = 30000;

const EarnMoneyFromSocket = () => {
  const { t } = useTranslation();
  const i18n_backend = (key) => t(`backend_response.${key}`);

  const [isVisible, setIsVisible] = useState(false);
  const [socketUrl, setSocketUrl] = useState(process.env.REACT_APP_WS_HOST);
  const [messages, setMessages] = useState([]);
  const [giveUpState, setGiveUpState] = useState({
    show: false,
    previousBalance: 0,
    withdrawalNumber: "",
  });

  const dispatch = useDispatch();
  const memberInfo = useSelector((state) => state.isMemberInfo);
  const isSocketMessage = useSelector((state) => state.isSocketMessage);

  const { message, show } = isSocketMessage || {};

  const { uid } = memberInfo || {};

  const { isLogin } = useCheckLogin();

  const earningSound = useSound({ customSound: "earning_money" });

  const didUnmount = useRef(false);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    queryParams: {
      key: "wsky",
    },
    onOpen: () => console.log("連線成功"),
    shouldReconnect: (closeEvent) => {
      /*
      useWebSocket will handle unmounting for you, but this is an example of a 
      case in which you would not want it to automatically reconnect
    */
      return didUnmount.current === false;
    },
    reconnectAttempts: 9999,
    reconnectInterval: 1000,
  });

  const socketReceivedData = useMemo(() => {
    if (lastMessage?.data) {
      return parseSomething(lastMessage.data);
    }
  }, [lastMessage?.timeStamp]);

  const compareMemberData = useMemo(() => {
    if (socketReceivedData?.member_info_uid == uid) {
      return true;
    }
    return false;
  }, [socketReceivedData, uid]);

  const handleConfirm = () => {
    dispatch(CLEAR_SOCKET_MESSAGE());
  };

  const responseSocketModal = useMemo(() => {
    if (show) {
      return (
        <CommonAlertModal
          openModal={show}
          setOpenModal={handleConfirm}
          priorityContent={i18n_backend(message)}
        />
      );
    }
  }, [show, message]);

  const responseComponents = useMemo(() => {
    return (
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Flex direction={"column"} gap="10px" alignItems={"center"}>
          {messages?.map((msg) => {
            const { amount = 0, id, isVisible } = msg;
            return (
              <ChakraBox
                key={id}
                variants={animationVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "exit"}
                bg={"rgba(0,0,0,0.5)"}
                px="30px"
                py="10px"
                borderRadius={"common"}
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                bgPosition="center"
                // onAnimationComplete={() => {
                //   setMessages((prev) => prev.filter((m) => m.id !== id));
                // }}
              >
                <Flex
                  gap="10px"
                  alignItems="center"
                  fontSize="90px"
                  fontWeight={900}
                  {...gradientStyle}
                >
                  <Text>+</Text>
                  <Text>
                    {/* <Count from={0} to={Number(amount)} /> */}
                    {Number(amount)}
                  </Text>
                </Flex>
              </ChakraBox>
            );
          })}
        </Flex>{" "}
      </Box>
    );
  }, [messages, dispatch]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    if (socketReceivedData) {
      const { type, member_info_uid, data, memo } = socketReceivedData;
      if (type === "deposit" && member_info_uid === uid && data != null) {
        const { amount, exchange } = data || {};
        if (amount == "error") {
          return dispatch(
            notice({
              title: i18n_backend("Server Error"),
              type: "error",
            })
          );
        }
        dispatch(storeCurrencyExchange(exchange || {}));
        new Audio(earningMusic).play();

        const currentTime = dayjs().unix(); // 使用秒級時間戳進行分組
        const messageWithId = {
          amount: amount,
          id: currentTime,
          isVisible: true,
        };

        setMessages((prev) => {
          // 檢查是否有相同 ID（相同秒數）的訊息
          const existingMessageIndex = prev.findIndex(
            (msg) => msg.id === messageWithId.id
          );

          if (existingMessageIndex !== -1) {
            // 如果找到相同 ID，合併 amount
            const updatedMessages = [...prev];
            updatedMessages[existingMessageIndex] = {
              ...updatedMessages[existingMessageIndex],
              amount: (
                Number(updatedMessages[existingMessageIndex].amount) +
                Number(messageWithId.amount)
              ).toString(),
              isVisible: true, // 確保合併後的訊息是可見的
            };
            return updatedMessages;
          } else {
            // 如果沒有相同 ID，添加新訊息
            return [...prev, messageWithId];
          }
        });

        setTimeout(() => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageWithId.id ? { ...msg, isVisible: false } : msg
            )
          );
          setTimeout(() => {
            setMessages((prev) =>
              prev.filter((msg) => msg.id !== messageWithId.id)
            );
          }, 500);
        }, 4000);
      } else if (
        type === "transCode" &&
        member_info_uid === uid &&
        data != null
      ) {
        if (data == "fails") {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: memo,
              show: true,
            })
          );
        } else {
          dispatch(CLEAR_SOCKET_MESSAGE());
          dispatch(triggerTransferGivenUpPoint(true));

          setTimeout(() => {
            //RESET
            dispatch(triggerTransferGivenUpPoint(false));
          }, 5000);
        }
      } else if (type === "error" && member_info_uid === uid && data != null) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data,
            show: true,
          })
        );
      } else if (type === "update_member") {
        if (isLogin) {
          dispatch({ type: "GET_MEMBER_DATA" });
        }
      } else if (type === "maintain_notice") {
        dispatch(
          SHOW_MAINTAIN_ALERT({
            state: true,
            maintainTime: data,
          })
        );
      } else if (type == "reload") {
        window.location.reload();
      } else if (type === "mac_giveup") {
        dispatch({ type: "GET_BALANCE" });
        setGiveUpState({
          show: true,
          previousBalance: data?.balance,
          withdrawalNumber: data?.withdrawal_number,
        });
      } else if (type === "mac_giveup_error") {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data?.message,
            show: true,
          })
        );
      }
    }
  }, [socketReceivedData, uid]);

  useEffect(() => {
    const heartbeat = setInterval(() => {
      sendMessage("ping");
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeat);
    };
  }, [sendMessage]);

  return (
    <Box zIndex={999999} position={"relative"}>
      {/* <AnimatePresence>{responseComponents}</AnimatePresence> */}
      <AnimatePresence>{responseComponents}</AnimatePresence>
      {responseSocketModal}

      {giveUpState.show && (
        <EndGameDetail
          onClose={() => {
            setGiveUpState({
              show: false,
              previousBalance: 0,
              withdrawalNumber: "",
            });
          }}
          previousBalance={giveUpState.previousBalance}
          transCode={""}
          withdrawalNumber={giveUpState.withdrawalNumber}
        />
      )}
    </Box>
  );
};

export default EarnMoneyFromSocket;
